import { useState } from 'react';
import { useT } from '@hana/i18n';
import { IconAlertTriangle, IconFileText, IconX } from '@tabler/icons-react';
import type { BlRelease } from './types';
import s from './dock.module.css';

// Bảng phải của Mint-BL-1920 (Việc · Duyệt · Thông báo). Công nợ chỉ hiện chữ Có / Không · Vượt / Trong mức (money = hide). Chỉ đọc.

type DockTab = 'task' | 'appr' | 'noti';

const TASKS: ReadonlyArray<readonly [string, string, string, string, boolean]> = [
  ['BLD', 'Draft chờ khách xác nhận · HNSGN260621750', 'Hạn 22/Oct 15:00', 'MAI', false],
  ['FBL', 'Gửi Final B/L · HNSGN260614690', 'Còn 40 phút', 'THUỶ', true],
  ['FBL', 'Gửi Final B/L · HNSGN260636080', 'Quá hạn 16 giờ', 'HÀ', true],
  ['LOI', 'LOI bản ký chưa nhận · HNSGN260637580', 'Trước ETA 08/Nov', 'THUỶ', false],
  ['RBL', 'Gửi Release Confirmation · 0109', 'Hạn +2h sau duyệt', 'HÀ', false],
];
const NOTIS: ReadonlyArray<readonly [string, string, string, boolean]> = [
  ['Khách YOSHIMI yêu cầu sửa Draft HNSGN260622360 (v2)', '09:12', 'var(--err)', true],
  ['ARRIVE yêu cầu release Telex qua mail', '08:12', 'var(--d-blue)', true],
  ['HANA Box: HBL HNSGN260616460 gửi ngoài luồng', '08:12', 'var(--d-amber)', true],
  ['ONE xác nhận surrender MBL ONEYSGNGZ4703800', 'Hôm qua 17:40', 'var(--d-green)', false],
  ['Mẫu LOI KMTC đổi bản mới từ 01/Oct', '20/Oct', 'var(--d-gray)', false],
];

interface Props {
  releases: readonly BlRelease[];
  warn: boolean;
  onWarnClose: () => void;
  onClose: () => void;
  onWrite: () => void;
  onOpenRelease: () => void;
}

export function BlDock({ releases, warn, onWarnClose, onClose, onWrite, onOpenRelease }: Props) {
  const t = useT();
  const [tab, setTab] = useState<DockTab>('task');
  const [done, setDone] = useState<Record<string, true>>({});
  const mine = releases.filter((r) => r.mine);
  const tabs: ReadonlyArray<{ key: DockTab; label: string; n: number }> = [
    { key: 'task', label: 'Việc', n: TASKS.length },
    { key: 'appr', label: 'Duyệt', n: mine.length },
    { key: 'noti', label: 'Thông báo', n: 3 },
  ];
  const groups = [
    { k: 'Quá hạn & gấp', urgent: true, items: TASKS.filter((x) => x[4]) },
    { k: 'Hôm nay', urgent: false, items: TASKS.filter((x) => !x[4]) },
  ];
  return (
    <aside className={s.dock}>
      {warn && (
        <div role="note" className={s.warn}>
          <div className={s.warnH}>
            <span className={s.warnIc} aria-hidden="true">
              <IconAlertTriangle size={14} stroke={2} />
            </span>
            <b>{t('bl.offFlow')}</b>
            <button type="button" className={s.x} aria-label={t('bl.hideWarn')} onClick={onWarnClose}>
              <IconX size={16} aria-hidden="true" />
            </button>
          </div>
          <div className={s.warnTx}>{t('bl.offFlowBox')}</div>
          <button type="button" className={s.ack} onClick={onWrite}>
            <IconFileText size={16} aria-hidden="true" />
            {t('bl.logToJob')}
          </button>
        </div>
      )}
      <div className={s.head}>
        <div role="tablist" aria-label="Bảng bên phải" className={s.tabs}>
          {tabs.map((t) => (
            <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} className={`${s.tab} ${tab === t.key ? s.on : ''}`} onClick={() => setTab(t.key)}>
              {t.label}
              <span className={`${s.nb} ${t.key === 'appr' && t.n ? s.nbPri : ''}`}>{t.n}</span>
            </button>
          ))}
        </div>
        <button type="button" className={s.close} aria-label={t('bl.closePanel')} onClick={onClose}>
          ×
        </button>
      </div>
      {tab === 'task' &&
        groups.map((g) => (
          <div key={g.k}>
            <div className={s.grp} style={{ color: g.urgent ? 'var(--err)' : 'var(--tx)' }}>
              <span>{g.k}</span>
              <span>{g.items.length}</span>
            </div>
            {g.items.map((x) => (
              <label key={x[1]} className={s.task} style={{ opacity: done[x[1]] ? 0.5 : 1 }}>
                <input type="checkbox" aria-label={`Xong: ${x[1]}`} checked={!!done[x[1]]} onChange={() => setDone((d) => (d[x[1]] ? {} : { ...d, [x[1]]: true }))} />
                <div>
                  <div style={{ textDecoration: done[x[1]] ? 'line-through' : 'none' }}>
                    <span className={s.code}>{x[0]}</span> {x[1]}
                  </div>
                  <div className={s.due}>
                    <span style={{ color: g.urgent ? 'var(--err)' : 'var(--lb)' }}>{x[2]}</span> · {x[3]}
                  </div>
                </div>
              </label>
            ))}
          </div>
        ))}
      {tab === 'appr' &&
        (mine.length === 0 ? (
          <div className={s.none}>{t('bl.noRelPending')}</div>
        ) : (
          mine.map((r) => (
            <div key={r.id} className={s.appr}>
              <div className={s.apprH}>
                <b>
                  Release {r.type} · {r.job}
                </b>
                <span>{r.when.split(' ')[1]}</span>
              </div>
              <div className={s.apprC}>{r.cus}</div>
              <div className={s.apprS}>{t('bl.myStep')} <b>{r.lvl.split(' · ')[0]}</b></div>
              {r.self && <span className={s.self}>{t('bl.selfApprove')}</span>}
              <div className={s.apprA}>
                <button type="button" className={s.ok} onClick={onWrite}>
                  {t('bl.approve')}
                </button>
                <button type="button" className={s.lnk} onClick={onOpenRelease}>
                  {t('bl.viewDetail')}
                </button>
              </div>
            </div>
          ))
        ))}
      {tab === 'noti' &&
        NOTIS.map((n, i) => (
          <div key={n[0]} className={s.noti} style={{ borderTopWidth: i ? 1 : 0 }}>
            <span className={s.nDot} style={{ background: n[2] }} />
            <div>
              <div style={{ fontWeight: n[3] ? 600 : 450 }}>{n[0]}</div>
              <div className={s.due}>{n[1]}</div>
            </div>
          </div>
        ))}
    </aside>
  );
}
